import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { EmployeePicker } from "@/components/EmployeePicker"
import { Badge, Button, Input, Select } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SHEET_WIDTH,
} from "@/components/Sheet"
import { fehlerNachricht, useToast } from "@/lib/toast"
import {
  EREIGNISSTATUS_LABEL,
  EREIGNISTYP_LABEL,
  createEreignis,
  fetchAnlagen,
  fetchEreignisLookupKomponenten,
  fetchEreignisLookupObjekte,
  fetchEreignisse,
  type Ereignisstatus,
  type Ereignistyp,
} from "@/lib/api"
import { EreignisDetailContent } from "./EreignisDetailPage"

const EREIGNISTYPEN: Ereignistyp[] = ["mangel", "stoerung", "missstand"]
const STATUS_OPTIONEN: Ereignisstatus[] = ["erledigt", "unerledigt", "ohne_serviceticket"]

const STATUS_TONE: Record<Ereignisstatus, "green" | "orange" | "slate"> = {
  erledigt: "green",
  unerledigt: "orange",
  ohne_serviceticket: "slate",
}

/**
 * Ereignis-Erfassung + gefilterte Liste (AP-4.4, 10-F1/F2/F3).
 *
 * Anlagen-/Objekt-/Komponentenbezug über gefilterte Lookups (R-A-04/R-A-10);
 * Schichtbezug wird als Snapshot (schichtDatum/schichtCode) übernommen —
 * ANNAHME A5 (kein Schichtplan-Replikat, ARCHITEKTUR.md §11): dieses
 * Formular übernimmt den Vorschlag NICHT automatisch (kein bestätigter
 * Personal-API-Endpoint für Schichtpläne), das Feld bleibt optionale
 * Freitext-/Auswahl-Eingabe für die meldende Person.
 */
export function EreignisPage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [filter, setFilter] = useState<{ ereignistyp?: string; status?: Ereignisstatus }>({})
  const [sheetOffen, setSheetOffen] = useState(false)
  const [detailEreignisId, setDetailEreignisId] = useState<number | null>(null)
  const [form, setForm] = useState({
    kurzbeschreibung: "",
    ereignistyp: "mangel" as Ereignistyp,
    melderEmployeeId: "",
    anlageId: "",
    objektId: "",
    komponenteId: "",
  })

  const { data: ereignisse, isLoading, isError, refetch } = useQuery({
    queryKey: ["ereignisse", filter],
    queryFn: () => fetchEreignisse(filter),
  })
  const { data: anlagen } = useQuery({ queryKey: ["anlagen"], queryFn: fetchAnlagen })

  const anlageId = form.anlageId ? Number(form.anlageId) : undefined
  const { data: objekte } = useQuery({
    queryKey: ["ereignis-lookup-objekte", anlageId],
    queryFn: () => fetchEreignisLookupObjekte(anlageId),
    enabled: anlageId !== undefined,
  })
  const { data: komponenten } = useQuery({
    queryKey: ["ereignis-lookup-komponenten", anlageId],
    queryFn: () => fetchEreignisLookupKomponenten(anlageId),
    enabled: anlageId !== undefined,
  })

  const anlegen = useMutation({
    mutationFn: () =>
      createEreignis({
        kurzbeschreibung: form.kurzbeschreibung,
        ereignistyp: form.ereignistyp,
        zeitpunkt: new Date().toISOString(),
        melderEmployeeId: form.melderEmployeeId,
        anlageId: Number(form.anlageId),
        objektId: form.objektId ? Number(form.objektId) : null,
        komponenteId: form.komponenteId ? Number(form.komponenteId) : null,
      }),
    onSuccess: () => {
      setForm({
        kurzbeschreibung: "",
        ereignistyp: "mangel",
        melderEmployeeId: "",
        anlageId: "",
        objektId: "",
        komponenteId: "",
      })
      setSheetOffen(false)
      queryClient.invalidateQueries({ queryKey: ["ereignisse"] })
      toast.success("Ereignis gemeldet.")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Ereignis konnte nicht gemeldet werden.")),
  })

  const formularGueltig = form.kurzbeschreibung.trim() && form.melderEmployeeId.trim() && form.anlageId

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Ereignismeldung</h1>
          <p className="mt-1 text-sm text-slate-600">
            Mängel, Störungen, Missstände melden (Ninox 10) — Status wird aus verknüpften
            Servicetickets berechnet.
          </p>
        </div>
        <Button type="button" onClick={() => setSheetOffen(true)}>
          + Ereignis melden
        </Button>
      </header>

      <Sheet open={sheetOffen} onOpenChange={setSheetOffen}>
        <SheetContent sheetKey="ereignis-neu" defaultWidth={SHEET_WIDTH.lg}>
          <SheetHeader>
            <SheetTitle>Ereignis melden</SheetTitle>
            <SheetDescription>Mangel, Störung oder Missstand erfassen (Ninox 10).</SheetDescription>
          </SheetHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (formularGueltig) anlegen.mutate()
            }}
          >
            <div className="space-y-1.5 sm:col-span-2">
              <label className="block text-sm text-slate-700">Kurzbeschreibung</label>
              <Input
                type="text"
                value={form.kurzbeschreibung}
                onChange={(e) => setForm((f) => ({ ...f, kurzbeschreibung: e.target.value }))}
                placeholder="Was ist passiert?"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm text-slate-700">Ereignistyp</label>
              <Select
                value={form.ereignistyp}
                onChange={(e) => setForm((f) => ({ ...f, ereignistyp: e.target.value as Ereignistyp }))}
              >
                {EREIGNISTYPEN.map((t) => (
                  <option key={t} value={t}>
                    {EREIGNISTYP_LABEL[t]}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <EmployeePicker
                value={form.melderEmployeeId || null}
                onChange={(id) => setForm((f) => ({ ...f, melderEmployeeId: id ?? "" }))}
                label="Meldende Person"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm text-slate-700">Anlage</label>
              <Select
                value={form.anlageId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, anlageId: e.target.value, objektId: "", komponenteId: "" }))
                }
                required
              >
                <option value="">— wählen —</option>
                {anlagen?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.bezeichnung}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm text-slate-700">Objekt (R-A-10, gefiltert)</label>
              <Select
                value={form.objektId}
                onChange={(e) => setForm((f) => ({ ...f, objektId: e.target.value }))}
                disabled={!form.anlageId}
              >
                <option value="">— optional —</option>
                {objekte?.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.bezeichnung}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm text-slate-700">Komponente (R-A-04, gefiltert)</label>
              <Select
                value={form.komponenteId}
                onChange={(e) => setForm((f) => ({ ...f, komponenteId: e.target.value }))}
                disabled={!form.anlageId}
              >
                <option value="">— optional —</option>
                {komponenten?.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.beschreibung}
                  </option>
                ))}
              </Select>
            </div>

            {anlegen.isError && (
              <p className="text-sm text-red-700 sm:col-span-2">Melden fehlgeschlagen.</p>
            )}

            <SheetFooter className="sm:col-span-2">
              <Button type="button" variant="secondary" onClick={() => setSheetOffen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" variant="primary" disabled={!formularGueltig || anlegen.isPending}>
                {anlegen.isPending ? "Speichert …" : "Ereignis melden"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Detail-Sheet von rechts (UX_KONZEPT.md §7) — Zeilen-Klick öffnet statt zu
          navigieren; `/ereignis/:id` bleibt als Deep-Link-Fallback. */}
      <Sheet open={detailEreignisId !== null} onOpenChange={(open) => !open && setDetailEreignisId(null)}>
        <SheetContent className="overflow-y-auto" sheetKey="ereignis-detail" defaultWidth={SHEET_WIDTH.xl}>
          {detailEreignisId !== null && <EreignisDetailContent ereignisId={detailEreignisId} />}
        </SheetContent>
      </Sheet>

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <Select
          value={filter.ereignistyp ?? ""}
          onChange={(e) => setFilter((f) => ({ ...f, ereignistyp: e.target.value || undefined }))}
          className="h-8 w-auto"
        >
          <option value="">Alle Typen</option>
          {EREIGNISTYPEN.map((t) => (
            <option key={t} value={t}>
              {EREIGNISTYP_LABEL[t]}
            </option>
          ))}
        </Select>
        <Select
          value={filter.status ?? ""}
          onChange={(e) =>
            setFilter((f) => ({ ...f, status: (e.target.value || undefined) as Ereignisstatus }))
          }
          className="h-8 w-auto"
        >
          <option value="">Alle Status</option>
          {STATUS_OPTIONEN.map((s) => (
            <option key={s} value={s}>
              {EREIGNISSTATUS_LABEL[s]}
            </option>
          ))}
        </Select>
        {(filter.ereignistyp || filter.status) && (
          <button
            type="button"
            className="rounded-md text-slate-500 underline hover:text-slate-800 focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 focus-visible:outline-none"
            onClick={() => setFilter({})}
          >
            Filter zurücksetzen
          </button>
        )}
        <Link to="/ereignis/dashboard" className="ml-auto text-slate-600 hover:underline">
          📊 Dashboard
        </Link>
      </div>

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Ereignisse …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-700">
          Ereignisse konnten nicht geladen werden.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md underline hover:no-underline focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 focus-visible:outline-none"
          >
            Erneut versuchen
          </button>
        </p>
      )}

      {ereignisse && (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-300 text-left text-slate-500">
              <th className="py-2 pr-4">Ereignis-ID</th>
              <th className="py-2 pr-4">Typ</th>
              <th className="py-2 pr-4">Kurzbeschreibung</th>
              <th className="py-2 pr-4">Zeitpunkt</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2">Tickets</th>
            </tr>
          </thead>
          <tbody>
            {ereignisse.map((e) => (
              <tr
                key={e.id}
                className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                onClick={() => setDetailEreignisId(e.id)}
              >
                <td className="py-2 pr-4 font-mono text-xs text-slate-500">{e.ereignisIdAnzeige}</td>
                <td className="py-2 pr-4 text-slate-600">{EREIGNISTYP_LABEL[e.ereignistyp]}</td>
                <td className="py-2 pr-4 text-slate-900">{e.kurzbeschreibung}</td>
                <td className="py-2 pr-4 text-slate-600">
                  {new Date(e.zeitpunkt).toLocaleString("de-DE")}
                </td>
                <td className="py-2 pr-4">
                  <Badge tone={STATUS_TONE[e.ereignisstatus]}>
                    {EREIGNISSTATUS_LABEL[e.ereignisstatus]}
                  </Badge>
                </td>
                <td className="py-2 text-slate-600">{e.anzahlServicetickets}</td>
              </tr>
            ))}
            {ereignisse.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-slate-400">
                  {filter.ereignistyp || filter.status
                    ? "Keine Ereignisse für diese Filter gefunden."
                    : `Noch keine Ereignisse gemeldet — mit „+ Ereignis melden" oben ein Ereignis melden.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}
